import React, { useState, useEffect, useRef, useCallback } from "react";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import "chartjs-adapter-moment";
import { ChartHeader, ChartSegmented, ResolutionToggle, applyChartTheme, CHART_THEME, sectionLineDataset, hoverGuidePlugin, targetLineAnnotation } from "./chartTheme";
import { QUEUE_TIME_GOOD_MAX } from "../concessionTheme";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from "chart.js";

ChartJS.register(zoomPlugin, annotationPlugin);


// Use the same colors as the overview component
const overviewColors = [
    '#3e58c0', // Indigo
    '#e88559', // Tangerine
    '#9792ec', // Lilac
    '#aa4851', // Scarlet
    '#866263', // Royal Purple
    '#76ba92', // Key Lime
    '#0bcaca', // Light Turquoise
    '#f1dc78', // Lemon Yellow
    '#3c6e5f', // Army Green
];

// Function to get color based on index
const getColorForIndex = (index) => {
  if (index < 0 || index >= overviewColors.length) {
    return '#000000';
  }
  return overviewColors[index];
};

// Function to convert game start time to minutes from start of day
const gameStartTimeToMinutes = (gameStartTime) => {
  if (!gameStartTime) return null;
  const [hours, minutes] = gameStartTime.split(':').map(Number);
  return hours * 60 + minutes;
};

// Function to convert timestamp to minutes, handling day boundary crossings
const timestampToMinutes = (timestamp) => {
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  
  // For times after midnight (0-7), add 24 hours worth of minutes
  if (hours >= 0 && hours < 8) {
    return (hours + 24) * 60 + minutes;
  } else {
    return hours * 60 + minutes;
  }
};

// Function to convert game start time to chart time (for day mode)
const convertGameStartTimeToChartTime = (gameStartTime) => {
  if (!gameStartTime) return null;
  
  const [hours, minutes] = gameStartTime.split(':').map(Number);
  let businessMinutes;
  
  // Use the same logic as in the data processing for day mode
  if (hours >= 8) {
    businessMinutes = (hours - 8) * 60 + minutes;
  } else if (hours < 3) {
    businessMinutes = (16 * 60) + (hours * 60) + minutes;
  } else {
    businessMinutes = (19 * 60) + ((hours - 3) * 60) + minutes;
  }
  
  // Convert to chart time using base date
  const baseDate = new Date();
  baseDate.setHours(8, 0, 0, 0);
  
  const chartTime = new Date(baseDate.getTime() + (businessMinutes * 60 * 1000));
  return chartTime.toISOString();
};

// Helper function to determine if this is transaction time data
const isTransactionTimeData = (yAxisLabel) => {
  if (!yAxisLabel) return false;
  const label = yAxisLabel.toLowerCase();
  return label.includes('transaction');
};

const ConcessionOverviewChart = ({ 
  multipleHistoricalData, // Array of historical data objects
  gameTitles, // Array of game titles
  gameStartTimes, // Array of game start times
  chartTitle = "Game Comparison",
  yAxisLabel = "Queue Time (mins)",
  isCountMode = false,
  timeRangeFilter = null, // { startOffset, endOffset, isFullRange } from ConcessionOverview
}) => {
  const [loading, setLoading] = useState(true);
  const [processedData, setProcessedData] = useState([]);
  const chartRef = useRef(null);
  const [chartResolution, setChartResolution] = useState(1);
  const [dataTimeRange, setDataTimeRange] = useState({ min: null, max: null });
  const [isNormalized, setIsNormalized] = useState(true);
  const resolutions = [1, 5, 10, 15, 30, 60];

  // Check if this is transaction time data
  const isTransactionTime = isTransactionTimeData(chartTitle);

  // Overflow markers only apply to queue data (not transaction, not count)
  const showOverflow = !isTransactionTime && !isCountMode;

  // Gets primary text color for chart
  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt').trim();
  
  const formatTransactionTime = useCallback((seconds) => {
    if (typeof seconds !== 'number' || seconds < 0) return '0s';
  
    const totalSeconds = Math.round(seconds);
  
    if (totalSeconds < 60) {
      return `${totalSeconds}`;
    }
  
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
  
    if (remainingSeconds === 0) {
      return `${minutes} mins`;
    }
  
    return `${minutes}m ${remainingSeconds}s`;
  }, []);

  const getIntervalKey = (timePoint, resolution) => {
    const roundedTime = new Date(timePoint);
    roundedTime.setMinutes(Math.floor(roundedTime.getMinutes() / resolution) * resolution, 0, 0);
    return roundedTime.toISOString();
  };

  // Function to calculate the time range from all data points
  const calculateDataTimeRange = useCallback((data) => {
    if (!data || data.length === 0) return { min: null, max: null };
    
    let minTime = null;
    let maxTime = null;
    
    data.forEach(gameData => {
      if (gameData.data && gameData.data.length > 0) {
        gameData.data.forEach(point => {
          const time = new Date(point.x);
          if (!minTime || time < minTime) minTime = time;
          if (!maxTime || time > maxTime) maxTime = time;
        });
      }
    });
    
    return { min: minTime, max: maxTime };
  }, []);

  // Resolution control functions

  useEffect(() => {
    setLoading(true);

    if (!multipleHistoricalData || multipleHistoricalData.length === 0) {
      setProcessedData([]);
      setLoading(false);
      return;
    }

    // Whether to apply the offset range filter (normalized mode only, and only
    // when a non-full range is set). Day Start mode has no game-start anchor.
    const applyRangeFilter =
      timeRangeFilter && !timeRangeFilter.isFullRange &&
      typeof timeRangeFilter.startOffset === 'number' &&
      typeof timeRangeFilter.endOffset === 'number';

    if (isNormalized && gameStartTimes) {
      // NORMALIZED PROCESSING (relative to game start)
      const normalizedGames = [];

      multipleHistoricalData.forEach((gameHistoricalData, gameIndex) => {
        const gameStartTime = gameStartTimes[gameIndex];
        const gameTitle = gameTitles ? gameTitles[gameIndex] : `Game ${gameIndex + 1}`;
        
        if (!gameStartTime || !gameHistoricalData || gameHistoricalData.length === 0) {
          return;
        }
        
        const gameStartMinutes = gameStartTimeToMinutes(gameStartTime);
        if (gameStartMinutes === null) return;
        
        // Set the center time as noon (12:00 PM) - this will represent game start
        const centerTime = new Date();
        centerTime.setHours(12, 0, 0, 0);

        // Aggregate all sections for this game
        const aggregatedData = new Map();

        gameHistoricalData.forEach(sectionData => {
          if (sectionData.data && sectionData.data.length > 0) {
            sectionData.data.forEach(point => {
              // Convert timestamp to minutes from start of day
              const itemMinutes = timestampToMinutes(point.x);
              
              // Calculate offset from game start time in minutes
              let offsetFromGameStart = itemMinutes - gameStartMinutes;

              // Apply the analysis time range filter (offsets from game start)
              if (applyRangeFilter &&
                  (offsetFromGameStart < timeRangeFilter.startOffset ||
                   offsetFromGameStart > timeRangeFilter.endOffset)) {
                return;
              }
              
              // Create normalized time centered around noon (game start)
              const normalizedTime = new Date(centerTime.getTime() + (offsetFromGameStart * 60 * 1000));
              const key = getIntervalKey(normalizedTime, chartResolution);
              
              if (!aggregatedData.has(key)) {
                aggregatedData.set(key, {
                  sum: 0,
                  count: 0,
                  max: 0,
                  min: Infinity,
                  overflow: 0,
                  offsetMinutes: offsetFromGameStart
                });
              }
              
              const entry = aggregatedData.get(key);
              entry.sum += point.y;
              entry.count += 1;
              entry.max = Math.max(entry.max, point.y);
              entry.min = Math.min(entry.min, point.y);
              // OR across the bucket: any overflowing reading marks the interval
              entry.overflow = Math.max(entry.overflow, point.overflow || 0);
            });
          }
        });

        const processedDataSet = Array.from(aggregatedData.entries()).map(([key, value]) => ({
          x: key,
          y: value.max,
          overflow: value.overflow,
          offsetMinutes: value.offsetMinutes
        }));

        // Sort by offset time to ensure proper line drawing
        processedDataSet.sort((a, b) => a.offsetMinutes - b.offsetMinutes);

        if (processedDataSet.length > 0) {
          normalizedGames.push({
            label: gameTitle,
            data: processedDataSet,
            gameIndex
          });
        }
      });

      setProcessedData(normalizedGames);
      
      // Calculate time range from processed data
      const timeRange = calculateDataTimeRange(normalizedGames);
      setDataTimeRange(timeRange);
    } else {
      // ORIGINAL PROCESSING (absolute time - normalized to same day)
      const processedGames = [];

      multipleHistoricalData.forEach((gameHistoricalData, gameIndex) => {
        const gameTitle = gameTitles ? gameTitles[gameIndex] : `Game ${gameIndex + 1}`;
        const gameStartTime = gameStartTimes ? gameStartTimes[gameIndex] : null;
        const gameStartMinutes = gameStartTime ? gameStartTimeToMinutes(gameStartTime) : null;
        
        if (!gameHistoricalData || gameHistoricalData.length === 0) {
          return;
        }

        // Use a fixed base date for all games (today)
        const baseDate = new Date();
        baseDate.setHours(8, 0, 0, 0); // Start at 8 AM

        // Aggregate all sections for this game
        const aggregatedData = new Map();

        gameHistoricalData.forEach(sectionData => {
          if (sectionData.data && sectionData.data.length > 0) {
            sectionData.data.forEach(point => {
              // Extract time from the original timestamp
              const originalDate = new Date(point.x);
              const hours = originalDate.getHours();
              const minutes = originalDate.getMinutes();

              // Apply the analysis range filter in Day Start mode too, when we can
              // compute an offset (requires this game's start time). Keeps the two
              // modes consistent; skipped if no start time is available.
              if (applyRangeFilter && gameStartMinutes !== null) {
                const itemMinutes = timestampToMinutes(point.x);
                const offsetFromGameStart = itemMinutes - gameStartMinutes;
                if (offsetFromGameStart < timeRangeFilter.startOffset ||
                    offsetFromGameStart > timeRangeFilter.endOffset) {
                  return;
                }
              }
              
              // Handle overnight times (like the traffic chart does)
              let businessMinutes;
              if (hours >= 8) {
                businessMinutes = (hours - 8) * 60 + minutes;
              } else if (hours < 3) {
                businessMinutes = (16 * 60) + (hours * 60) + minutes;
              } else {
                businessMinutes = (19 * 60) + ((hours - 3) * 60) + minutes;
              }
              
              // Create normalized time on the base date
              const normalizedTime = new Date(baseDate.getTime() + (businessMinutes * 60 * 1000));
              const key = getIntervalKey(normalizedTime, chartResolution);
              
              if (!aggregatedData.has(key)) {
                aggregatedData.set(key, {
                  sum: 0,
                  count: 0,
                  max: 0,
                  min: Infinity,
                  overflow: 0,
                  originalTimes: [], // Store all original times for this interval
                  maxOriginalTime: null // Store the original time that corresponds to the max value
                });
              }
              const entry = aggregatedData.get(key);
              entry.sum += point.y;
              entry.count += 1;
              entry.originalTimes.push(originalDate);
              // OR across the bucket: any overflowing reading marks the interval
              entry.overflow = Math.max(entry.overflow, point.overflow || 0);
              
              // If this is the new max value, store its original time
              if (point.y > entry.max) {
                entry.max = point.y;
                entry.maxOriginalTime = originalDate;
              } else if (point.y === entry.max && entry.maxOriginalTime === null) {
                entry.maxOriginalTime = originalDate;
              }
              
              entry.min = Math.min(entry.min, point.y);
            });
          }
        });

        const aggregatedPoints = Array.from(aggregatedData.entries()).map(([key, value]) => ({
          x: key,
          y: value.max,
          overflow: value.overflow,
          originalTime: value.maxOriginalTime, // Use the original time that corresponds to the max value
          dataPointCount: value.count // Include count for debugging
        }));

        const sortedPoints = aggregatedPoints.sort((a, b) => new Date(a.x) - new Date(b.x));

        if (sortedPoints.length > 0) {
          processedGames.push({
            label: gameTitle,
            data: sortedPoints,
            gameIndex
          });
        }
      });

      setProcessedData(processedGames);
      
      // Calculate time range from processed data
      const timeRange = calculateDataTimeRange(processedGames);
      setDataTimeRange(timeRange);
    }
    
    setLoading(false);
  }, [multipleHistoricalData, gameTitles, gameStartTimes, chartResolution, isNormalized, calculateDataTimeRange, timeRangeFilter]);

  const chartData = {
    datasets: processedData.map((gameData) => ({
      label: gameData.label,
      // Overflow only applies to queue data (not transaction, not count)
      data: gameData.data.map(point => ({ x: point.x, y: point.y, overflow: showOverflow ? point.overflow : 0 })),
      ...sectionLineDataset(getColorForIndex(gameData.gameIndex)),
    }))
  };

  const maxValue = Math.max(
    ...processedData.flatMap(dataset => 
      dataset.data.map(point => point.y)
    ),
    isCountMode ? 50 : 30
  );

  // Create center line at noon (game start time) - only used in normalized mode
  const centerTime = new Date();
  centerTime.setHours(12, 0, 0, 0);

  const chartOptions = {
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'hour',
          displayFormats: {
            hour: isNormalized ? 'h A' : 'h:mm A'
          },
          stepSize: chartResolution >= 30 ? 1 : 0.5,
          tooltipFormat: isNormalized ? 'h:mm A' : undefined,
        },
        title: {
          color: primaryTextColor,
          display: true,
          text: isNormalized ? "Time Relative to Game Start" : "Time",
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: false,
        },
        ticks: {
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          ...(isNormalized && {
            callback: function(value, index) {
              const date = new Date(value);
              
              // Calculate hours offset from center (game start)
              const totalMinutesFromCenter = (date.getTime() - centerTime.getTime()) / (1000 * 60);
              const hoursOffset = Math.round(totalMinutesFromCenter / 60);
              
              if (hoursOffset === 0) return "Game Start";
              else if (hoursOffset > 0) return `+${hoursOffset}h`;
              else return `${hoursOffset}h`;
            }
          })
        },
        min: dataTimeRange.min,
        max: dataTimeRange.max
      },
      y: {
        min: 0,
        max: Math.ceil(maxValue / (isCountMode ? 10 : 5)) * (isCountMode ? 10 : 5),
        display: true,
        title: {
          color: primaryTextColor,
          display: true,
          text: yAxisLabel,
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: true,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          stepSize: isCountMode 
            ? Math.max(5, Math.ceil(maxValue / 100) * 5) 
            : 5,
          callback: function (value) {
            return formatTransactionTime 
              ? formatTransactionTime(value) 
              : `${value}`;
          }
        }
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'nearest', // Changed from 'index' to 'nearest'
        intersect: false,
        // Add a filter to only show tooltips for datasets that actually have data at that point
        filter: function(tooltipItem, index, tooltipItems, chart) {
          // Safety check for chart and data existence
          if (!chart || !chart.data || !chart.data.datasets) {
            return true;
          }
          
          // In Day Start mode, be more strict about showing only actual data points
          if (!isNormalized) {
            const dataset = chart.data.datasets[tooltipItem.datasetIndex];
            if (!dataset || !dataset.data) {
              return false;
            }
            const dataPoint = dataset.data[tooltipItem.dataIndex];
            return dataPoint && dataPoint.y !== undefined && dataPoint.y !== null;
          }
          return true; // In normalized mode, show all as before
        },
        callbacks: isNormalized ? {
          title: (context) => {
            if (context.length === 0) return "";
            
            const dataIndex = context[0].dataIndex;
            const datasetIndex = context[0].datasetIndex;
            const dataset = processedData[datasetIndex];
            
            if (dataset && dataset.data[dataIndex]) {
              const offsetMinutes = dataset.data[dataIndex].offsetMinutes;
              const hours = Math.floor(Math.abs(offsetMinutes) / 60);
              const minutes = Math.abs(offsetMinutes) % 60;
              const sign = offsetMinutes >= 0 ? '+' : '-';
              
              return `${context[0].dataset.label}\n${sign}${hours}h ${minutes}m from Game Start`;
            }
            
            return context[0].dataset.label;
          },
          label: (context) => {
            const value = context.parsed.y;
            const isOverflow = showOverflow && context.raw?.overflow;
            const overflowNote = isOverflow ? ' (overflow)' : '';
            if (isCountMode) {
              const unit = value === 1 ? 'person' : 'people';
              return ` ${value} ${unit}`;
            } else if (isTransactionTime) {
              // Show seconds for transaction times
              return ` ${formatTransactionTime(value)}`;
            } else {
              return ` ${value}${isOverflow ? '+' : ''} mins${overflowNote}`;
            }
          },
        } : {
          title: (context) => {
            if (context.length === 0) return "";
            
            const dataIndex = context[0].dataIndex;
            const datasetIndex = context[0].datasetIndex;
            const dataset = processedData[datasetIndex];
            const label = context[0].dataset.label;
            
            // In Day Start mode, show the original time from the data
            if (dataset && dataset.data[dataIndex] && dataset.data[dataIndex].originalTime) {
              const originalTime = new Date(dataset.data[dataIndex].originalTime);
              const formattedTime = originalTime.toLocaleTimeString('en-US', { 
                hour: 'numeric',
                minute: '2-digit'
              });
              return `${label}\n${formattedTime}`;
            }
            
            // Fallback: extract time from the chart's x value (like traffic chart does)
            const labelParts = context[0].label.split(',');  
            const pulledTime = labelParts.length > 2 ? labelParts[2].trim() : '';
            
            let formattedTime;
            if (pulledTime) {
              const timeFormatter = new Date(`2000-01-01 ${pulledTime}`);
              formattedTime = timeFormatter.toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit'
              });
            } else {
              const date = new Date(context[0].parsed.x);
              formattedTime = date.toLocaleTimeString('en-US', { 
                hour: 'numeric',
                minute: '2-digit'
              });
            }
            
            return `${label}\n${formattedTime}`;
          },
          label: (context) => {
            const value = context.parsed.y;
            const isOverflow = showOverflow && context.raw?.overflow;
            const overflowNote = isOverflow ? ' (overflow)' : '';
            if (isCountMode) {
              const unit = value === 1 ? 'person' : 'people';
              return ` ${value} ${unit}`;
            } else if (isTransactionTime) {
              // Show seconds for transaction times
              return ` ${value} secs`;
            } else {
              return ` ${value}${isOverflow ? '+' : ''} mins${overflowNote}`;
            }
          },
        },
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      zoom: {
        limits: {
          x: { 
            min: dataTimeRange.min, 
            max: dataTimeRange.max, 
            minRange: 60 * 60 * 1000
          },
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      },
      annotation: {
        annotations: isNormalized ? {
          gameStartLine: {
            type: 'line',
            mode: 'vertical',
            scaleID: 'x',
            value: centerTime.toISOString(),
            borderColor: CHART_THEME.gameStart,
            borderWidth: 2,
            borderDash: [8, 4],
          },
          ...(showOverflow ? { target: targetLineAnnotation(QUEUE_TIME_GOOD_MAX, `${QUEUE_TIME_GOOD_MAX} min target`) } : {}),
        } : (gameStartTimes && gameStartTimes.length > 0 ? 
          // Day mode: multiple game start lines (one for each selected game)
          gameStartTimes.reduce((annotations, startTime, index) => {
            const chartTime = convertGameStartTimeToChartTime(startTime);
            if (!chartTime) return annotations;
            
            annotations[`gameStart${index}`] = {
              type: 'line',
              mode: 'vertical',
              scaleID: 'x',
              value: chartTime,
              borderColor: getColorForIndex(processedData[index]?.gameIndex ?? index),
              borderWidth: 1.5,
              borderDash: [6, 4],
              label: {
                content: `Game Start: ${startTime}`,
                enabled: true,
                position: 'top',
                backgroundColor: getColorForIndex(processedData[index]?.gameIndex ?? index),
                color: 'white',
                fontSize: 10,
              }
            };
            return annotations;
          }, showOverflow ? { target: targetLineAnnotation(QUEUE_TIME_GOOD_MAX, `${QUEUE_TIME_GOOD_MAX} min target`) } : {}) : {}),
      },
    },
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '60px', 
        textAlign: 'center', 
        color: '#64748b', 
        fontSize: '18px' 
      }}>
        Loading chart data...
      </div>
    );
  }

  if (processedData.length === 0) {
    return (
      <div style={{ 
        padding: '60px', 
        textAlign: 'center', 
        color: '#64748b', 
        fontSize: '18px' 
      }}>
        No data available for selected games
      </div>
    );
  }

  return (
    <div className="graph panel-2 cx-chart-card">
      <ChartHeader
        title={chartTitle}
        subtitle={showOverflow ? "Selected games compared side by side. Each point is the highest reading in that interval. Red segments mark overflow." : "Selected games compared side by side. Each point is the highest reading in that interval."}
        controls={<>
            <ChartSegmented
              label="Align by"
              options={[{ value: true, label: "Game Start" }, { value: false, label: "Day Start" }]}
              value={isNormalized}
              onChange={setIsNormalized}
            />
            <ResolutionToggle resolutions={resolutions} value={chartResolution} onChange={setChartResolution} />
          </>}
      />
      <div style={{ position: 'relative', height: '400px', marginTop: '8px' }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflowX: 'auto',
        }}>
          <div style={{ width: '100%', height: '100%' }}>
            <Line ref={chartRef} data={chartData} options={applyChartTheme(chartOptions)} plugins={[hoverGuidePlugin]} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConcessionOverviewChart;